import React, { useState } from "react";
import { toast } from "react-toastify";
import { getAssignVendorTimeline } from "../../../services/paymentLogApi";
import { fileUrl } from "../../../services/assignVendorApi";
import {
  ACTION_ICONS,
  ACTION_LABELS,
  actionBadgeClass,
  formatDate,
  formatDateTime,
} from "./paymentLogUtils";

/**
 * Collapsible "Payment history" for one vendor assignment (newest first).
 * Uses <details> so it still toggles inside the bulk-edit page's disabled <fieldset> (view-only mode).
 */
const PaymentTimeline = ({ assignVendorId }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!assignVendorId || loading) return;
    setLoading(true);
    getAssignVendorTimeline(assignVendorId)
      .then((list) => setItems([...list].reverse()))
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || err.message || "Could not load payment history.",
          { position: "top-right", autoClose: 4000 },
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <details
      className="border rounded p-3"
      onToggle={(e) => {
        if (e.currentTarget.open && items === null) load();
      }}
    >
      <summary className="text-muted fw-semibold" style={{ fontSize: 13, cursor: "pointer" }}>
        <i className="fa fa-history me-1" aria-hidden />
        Payment history
      </summary>
      <div className="mt-3">
        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        ) : !items || items.length === 0 ? (
          <div className="small text-muted">No history recorded yet.</div>
        ) : (
          <ul className="list-unstyled mb-0 ps-3 border-start">
            {items.map((log) => (
              <li key={log._id} className="position-relative mb-3 ps-3">
                <span
                  className={`badge rounded-circle position-absolute ${actionBadgeClass(log.action, log.toStatus)}`}
                  style={{ left: -27, top: 0, width: 24, height: 24, lineHeight: "16px" }}
                >
                  <i className={`fa ${ACTION_ICONS[log.action] || "fa-circle"}`} aria-hidden />
                </span>
                <div className="small fw-semibold">{ACTION_LABELS[log.action] || log.action}</div>
                <div className="small text-muted">
                  by {log.actorName || "System"}
                  {log.actorRole ? ` (${log.actorRole})` : ""} · {formatDateTime(log.createdAt)}
                </div>
                {log.reason ? <div className="small mt-1">{log.reason}</div> : null}
                {log.paymentDate || log.paymentReference ? (
                  <div className="small mt-1">
                    {log.paymentDate ? <>Payment date: {formatDate(log.paymentDate)}</> : null}
                    {log.paymentDate && log.paymentReference ? " · " : null}
                    {log.paymentReference ? <>Ref: {log.paymentReference}</> : null}
                  </div>
                ) : null}
                {Array.isArray(log.attachments) && log.attachments.length > 0 ? (
                  <div className="small mt-1">
                    {log.attachments.map((f) => (
                      <a
                        key={f.path}
                        href={fileUrl(f.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="me-2"
                      >
                        <i className="fa fa-paperclip me-1" aria-hidden />
                        {f.originalName || "slip"}
                      </a>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
};

export default PaymentTimeline;
